import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import {
  Brain, ChevronDown, ChevronUp, RefreshCw, Loader2,
  AlertTriangle, FileSearch, ArrowRight, Zap, Shield, AlertCircle,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * MatterIntelligenceCard
 *
 * Props:
 *   matterId  — string ID of the matter
 *   fileCount — number of knowledge files belonging to this matter
 *
 * Fetches existing intelligence on mount.
 * Shows "Analyse Now" if >= 2 files but no report yet.
 * Shows "Need 2+ files" prompt otherwise.
 */
export default function MatterIntelligenceCard({ matterId, fileCount = 0 }) {
  const { getAuthHeaders } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [expanded, setExpanded] = useState({});

  const loadReport = useCallback(async () => {
    if (!matterId) return;
    try {
      const resp = await fetch(`${API}/knowledge/matter-intelligence/${matterId}`, {
        headers: getAuthHeaders(), credentials: 'include',
      });
      if (resp.ok) setReport(await resp.json());
      else if (resp.status !== 404) toast.error('Failed to load matter intelligence');
    } catch {}
    finally { setLoading(false); }
  }, [matterId, getAuthHeaders]);

  useEffect(() => { loadReport(); }, [loadReport]);

  const generate = async () => {
    setGenerating(true);
    try {
      const resp = await fetch(`${API}/knowledge/matter-intelligence/${matterId}`, {
        method: 'POST', headers: getAuthHeaders(), credentials: 'include',
      });
      if (resp.ok) {
        toast.success('Generating matter intelligence…');
        // Poll every 3s for up to 30s
        let tries = 0;
        const poll = setInterval(async () => {
          tries++;
          await loadReport();
          if (report || tries >= 10) {
            clearInterval(poll);
            setGenerating(false);
          }
        }, 3000);
      }
    } catch { toast.error('Failed to start generation'); setGenerating(false); }
  };

  const toggle = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  if (loading) {
    return (
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '16px' }}>
          <Loader2 size={14} className="animate-spin" style={{ color: 'var(--text-muted)' }} />
          <span style={mutedText}>Loading matter intelligence…</span>
        </div>
      </div>
    );
  }

  if (fileCount < 2 && !report) {
    return (
      <div style={cardStyle}>
        <div style={{ padding: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Brain size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
          <span style={{ ...mutedText, fontSize: '12px' }}>
            Upload 2 or more files to this matter to enable Matter Intelligence.
          </span>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div style={cardStyle}>
        <div style={{ padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Brain size={16} style={{ color: 'var(--accent-primary)', flexShrink: 0 }} />
            <div>
              <p style={{ fontFamily: 'var(--font-ui)', fontWeight: 600, fontSize: '13px', color: 'var(--text-primary)', margin: 0 }}>Matter Intelligence available</p>
              <p style={{ ...mutedText, fontSize: '11px', margin: '2px 0 0' }}>AI analysis of all {fileCount} files in this matter</p>
            </div>
          </div>
          <button onClick={generate} disabled={generating} style={primaryBtnStyle}>
            {generating ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} />}
            Analyse Now
          </button>
        </div>
      </div>
    );
  }

  const sections = [
    { key: 'timeline',              label: 'Timeline',           icon: <ArrowRight size={12} />, color: '#3b82f6',  items: (report.timeline || []).map(t => `${t.date}: ${t.event}`) },
    { key: 'legalIssues',           label: 'Legal Issues',       icon: <Brain size={12} />,      color: '#6366f1',  items: report.legalIssues || [] },
    { key: 'contradictions',        label: 'Contradictions',     icon: <AlertCircle size={12} />,color: '#ef4444',  items: report.contradictions || [] },
    { key: 'missingDocuments',      label: 'Missing Documents',  icon: <FileSearch size={12} />, color: '#f59e0b',  items: report.missingDocuments || [] },
    { key: 'recommendedNextSteps',  label: 'Next Steps',         icon: <ArrowRight size={12} />, color: '#10b981',  items: report.recommendedNextSteps || [] },
    { key: 'strengthsOfCase',       label: 'Strengths',          icon: <Shield size={12} />,     color: '#10b981',  items: report.strengthsOfCase || [] },
    { key: 'weaknessesOfCase',      label: 'Weaknesses',         icon: <AlertTriangle size={12} />, color: '#ef4444', items: report.weaknessesOfCase || [] },
  ];
  const vakalatBase = report.vakalatBase || {};
  const hearingReadiness = vakalatBase.hearingReadiness || report.hearingReadiness || {};
  const cockpitSections = [
    { key: 'riskRadar', label: 'Risk Radar', icon: <AlertTriangle size={12} />, color: '#ef4444', items: vakalatBase.riskRadar || report.riskRadar || [] },
    { key: 'missingLinks', label: 'Missing Links', icon: <FileSearch size={12} />, color: '#f59e0b', items: vakalatBase.missingLinks || report.missingLinks || [] },
    { key: 'nextMoves', label: 'Next Moves', icon: <ArrowRight size={12} />, color: '#10b981', items: vakalatBase.nextMoves || report.nextMoves || [] },
    { key: 'proofPack', label: 'Proof Pack', icon: <Shield size={12} />, color: '#3b82f6', items: vakalatBase.proofPack || report.proofPack || [] },
    { key: 'opponentSignals', label: 'Opponent Signals', icon: <AlertCircle size={12} />, color: '#8b5cf6', items: vakalatBase.opponentSignals || report.opponentSignals || [] },
  ];

  return (
    <div style={cardStyle}>
      {/* Header */}
      <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Brain size={15} style={{ color: 'var(--accent-primary)' }} />
          <span style={{ fontFamily: 'var(--font-ui)', fontWeight: 700, fontSize: '13px', color: 'var(--text-primary)' }}>Matter Intelligence</span>
          <span style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '3px', background: 'rgba(46,204,90,0.1)', color: 'var(--accent-primary)', fontFamily: 'var(--font-ui)' }}>
            {report.fileCount} files analysed
          </span>
        </div>
        <div style={{ display: 'flex', gap: '6px' }}>
          <button onClick={generate} disabled={generating} title="Regenerate" style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '5px', border: '1px solid var(--border-default)', background: 'transparent', color: 'var(--text-muted)', fontSize: '11px', cursor: 'pointer', fontFamily: 'var(--font-ui)' }}>
            {generating ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
            Regenerate
          </button>
        </div>
      </div>

      {/* Overview */}
      {report.overview && (
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
          <p style={{ fontSize: '12px', color: 'var(--text-secondary)', fontFamily: 'var(--font-ui)', lineHeight: 1.6, margin: 0 }}>{report.overview}</p>
        </div>
      )}

      {(hearingReadiness.score || vakalatBase.clientReadyUpdate || (vakalatBase.strategicThemes || []).length > 0) && (
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {hearingReadiness.score ? (
            <div style={{ display: 'grid', gridTemplateColumns: '84px 1fr', gap: '12px', alignItems: 'center' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '72px', borderRadius: '14px', background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                <span style={{ fontSize: '22px', lineHeight: 1, fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-display)' }}>{hearingReadiness.score}</span>
                <span style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '6px', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: 'var(--font-ui)', fontWeight: 700 }}>Readiness</span>
              </div>
              <div>
                <p style={{ margin: 0, fontSize: '12px', fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-ui)' }}>
                  {hearingReadiness.label || 'Matter posture'}
                </p>
                {vakalatBase.matterPulse?.summary && (
                  <p style={{ margin: '6px 0 0', fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.6, fontFamily: 'var(--font-ui)' }}>
                    {vakalatBase.matterPulse.summary}
                  </p>
                )}
                {(hearingReadiness.readySignals || hearingReadiness.missingSignals) && (
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
                    {(hearingReadiness.readySignals || []).slice(0, 2).map((item, index) => (
                      <span key={`ready-${index}`} style={{ fontSize: '10px', padding: '4px 8px', borderRadius: '999px', background: 'rgba(46,204,90,0.08)', color: 'var(--accent-primary)', fontFamily: 'var(--font-ui)', fontWeight: 600 }}>
                        {item}
                      </span>
                    ))}
                    {(hearingReadiness.missingSignals || []).slice(0, 2).map((item, index) => (
                      <span key={`missing-${index}`} style={{ fontSize: '10px', padding: '4px 8px', borderRadius: '999px', background: 'rgba(245,158,11,0.12)', color: '#b45309', fontFamily: 'var(--font-ui)', fontWeight: 600 }}>
                        {item}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : null}

          {(vakalatBase.strategicThemes || []).length > 0 && (
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {(vakalatBase.strategicThemes || []).slice(0, 6).map((theme) => (
                <span key={theme} style={{ fontSize: '10px', padding: '4px 8px', borderRadius: '999px', background: 'var(--bg-elevated)', color: 'var(--text-secondary)', border: '1px solid var(--border-subtle)', fontFamily: 'var(--font-ui)' }}>
                  {theme}
                </span>
              ))}
            </div>
          )}

          {vakalatBase.clientReadyUpdate && (
            <div style={{ padding: '10px 12px', borderRadius: '12px', background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
              <p style={{ margin: 0, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-muted)', fontWeight: 700, fontFamily: 'var(--font-ui)' }}>
                Client Explain Mode
              </p>
              <p style={{ margin: '6px 0 0', fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.6, fontFamily: 'var(--font-ui)' }}>
                {vakalatBase.clientReadyUpdate}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Key parties */}
      {report.keyParties?.length > 0 && (
        <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'var(--font-ui)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', flexShrink: 0 }}>Parties:</span>
          {report.keyParties.map((p, i) => (
            <span key={i} style={{ fontSize: '11px', padding: '2px 7px', borderRadius: '4px', background: 'var(--bg-elevated)', color: 'var(--text-secondary)', border: '1px solid var(--border-subtle)', fontFamily: 'var(--font-ui)' }}>{p}</span>
          ))}
        </div>
      )}

      {cockpitSections.map(sec => {
        if (!sec.items?.length) return null;
        const isOpen = expanded[sec.key];
        return (
          <div key={sec.key} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
            <button
              onClick={() => toggle(sec.key)}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ color: sec.color }}>{sec.icon}</span>
                <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)', fontFamily: 'var(--font-ui)' }}>{sec.label}</span>
                <span style={{ fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'var(--font-ui)' }}>({sec.items.length})</span>
              </div>
              {isOpen ? <ChevronUp size={13} style={{ color: 'var(--text-muted)' }} /> : <ChevronDown size={13} style={{ color: 'var(--text-muted)' }} />}
            </button>
            {isOpen && (
              <div style={{ padding: '0 16px 12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {sec.items.map((item, index) => (
                  <div key={item.id || `${sec.key}-${index}`} style={{ padding: '10px 12px', borderRadius: '10px', background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
                      <p style={{ margin: 0, fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)', fontFamily: 'var(--font-ui)' }}>
                        {item.title || item.description || 'Untitled'}
                      </p>
                      {item.severity && (
                        <span style={{
                          fontSize: '10px',
                          padding: '3px 8px',
                          borderRadius: '999px',
                          background: item.severity === 'high' ? 'rgba(239,68,68,0.12)' : 'rgba(245,158,11,0.12)',
                          color: item.severity === 'high' ? '#dc2626' : '#b45309',
                          fontFamily: 'var(--font-ui)',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                        }}>
                          {item.severity}
                        </span>
                      )}
                    </div>
                    {item.description && (
                      <p style={{ margin: '6px 0 0', fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.55, fontFamily: 'var(--font-ui)' }}>
                        {item.description}
                      </p>
                    )}
                    {item.citation?.label || item.citation?.quote ? (
                      <div style={{ marginTop: '8px', padding: '8px 10px', borderRadius: '8px', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}>
                        <p style={{ margin: 0, fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: 'var(--font-ui)', fontWeight: 700 }}>
                          {item.citation.label || 'Source proof'}
                        </p>
                        <p style={{ margin: '5px 0 0', fontSize: '11px', color: 'var(--text-secondary)', lineHeight: 1.55, fontFamily: 'var(--font-ui)' }}>
                          {item.citation.quote || item.citation.snippet}
                        </p>
                      </div>
                    ) : null}
                    {item.previewUrl && (
                      <a href={item.previewUrl} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', marginTop: '8px', fontSize: '11px', fontWeight: 600, color: 'var(--accent-primary)', textDecoration: 'none', fontFamily: 'var(--font-ui)' }}>
                        Open source proof
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Expandable sections */}
      {sections.map(sec => {
        if (!sec.items?.length) return null;
        const isOpen = expanded[sec.key];
        return (
          <div key={sec.key} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
            <button
              onClick={() => toggle(sec.key)}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ color: sec.color }}>{sec.icon}</span>
                <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)', fontFamily: 'var(--font-ui)' }}>{sec.label}</span>
                <span style={{ fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'var(--font-ui)' }}>({sec.items.length})</span>
              </div>
              {isOpen ? <ChevronUp size={13} style={{ color: 'var(--text-muted)' }} /> : <ChevronDown size={13} style={{ color: 'var(--text-muted)' }} />}
            </button>
            {isOpen && (
              <div style={{ padding: '0 16px 12px' }}>
                <ul style={{ margin: 0, paddingLeft: '16px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {sec.items.map((item, i) => (
                    <li key={i} style={{ fontSize: '12px', color: 'var(--text-secondary)', fontFamily: 'var(--font-ui)', lineHeight: 1.55 }}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );
      })}

      {report.generatedAt && (
        <div style={{ padding: '8px 16px' }}>
          <span style={{ fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'var(--font-ui)' }}>
            Generated {new Date(report.generatedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
          </span>
        </div>
      )}
    </div>
  );
}

// ── Style constants ───────────────────────────────────────────────────────────
const cardStyle = {
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-subtle)',
  borderRadius: '10px', overflow: 'hidden',
};

const mutedText = {
  fontSize: '12px', color: 'var(--text-muted)',
  fontFamily: 'var(--font-ui)',
};

const primaryBtnStyle = {
  display: 'inline-flex', alignItems: 'center', gap: '5px',
  padding: '6px 14px', borderRadius: '6px', border: 'none',
  background: 'var(--accent-primary)', color: 'var(--accent-foreground)',
  fontSize: '12px', fontWeight: 600, cursor: 'pointer',
  fontFamily: 'var(--font-ui)',
};
